import { DialogComponent } from '@theme/dialog';

/**
 * A custom element that manages a search drawer.
 * Overrides showDialog to use show() instead of showModal() so header remains clickable
 *
 * @extends {DialogComponent}
 */
class SearchDrawerComponent extends DialogComponent {
  #previousScrollY = 0;

  /**
   * Override showDialog to use show() instead of showModal() so header remains clickable
   */
  showDialog() {
    const { dialog } = this.refs;

    if (dialog.open) return;

    // If body is already fixed (another drawer was open), preserve scroll position from body.style.top
    // Otherwise, use current window scroll position
    let scrollY;
    if (document.body.style.position === 'fixed' && document.body.style.top) {
      scrollY = -parseInt(document.body.style.top) || 0;
    } else {
      scrollY = window.scrollY;
    }
    this.#previousScrollY = scrollY;

    // Prevent layout thrashing by separating DOM reads from DOM writes
    requestAnimationFrame(() => {
      document.body.style.width = '100%';
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;

      // Use show() instead of showModal() so header can remain clickable above the dialog
      dialog.show();
      this.dispatchEvent(new CustomEvent('dialog:open'));

      // Attach click handler directly to backdrop
      const backdrop = this.querySelector('.search-modal__backdrop');
      if (backdrop) {
        backdrop.addEventListener('click', this.#handleBackdropClick);
      }

      // Handle escape key at document level
      document.addEventListener('keydown', this.#handleKeyDown);
    });
  }

  /**
   * Handle clicks to close dialog when clicking on backdrop
   * @param {MouseEvent} event
   */
  #handleBackdropClick = (/** @type {MouseEvent} */ event) => {
    if (event.target === event.currentTarget) {
      this.closeDialog();
    }
  };

  /**
   * Handle escape key to close dialog
   * @param {KeyboardEvent} event
   */
  #handleKeyDown = (/** @type {KeyboardEvent} */ event) => {
    if (event.key !== 'Escape') return;
    if (!this.refs.dialog.open) return;

    event.preventDefault();
    event.stopPropagation();
    this.closeDialog();
  };

  /**
   * Override closeDialog to restore scroll position
   */
  closeDialog = async () => {
    const { dialog } = this.refs;

    if (!dialog.open) return;

    // Check if another drawer is opening (to preserve scroll position during transitions)
    // Check if drawer coordinator is transitioning OR if another drawer is actually open
    const coordinator = window.drawerCoordinator;
    const isAnotherDrawerOpening = document.body.hasAttribute('data-drawer-transitioning') || 
      (coordinator && coordinator.hasAnyDrawerOpen());

    // Remove event listeners
    const backdrop = this.querySelector('.search-modal__backdrop');
    if (backdrop) {
      backdrop.removeEventListener('click', this.#handleBackdropClick);
    }
    document.removeEventListener('keydown', this.#handleKeyDown);

    dialog.classList.add('dialog-closing');

    // Wait for transition to complete
    const transitionDuration = getComputedStyle(dialog).getPropertyValue('--animation-speed') || '0.3s';
    const durationMs = parseFloat(transitionDuration) * (transitionDuration.includes('ms') ? 1 : 1000);

    await new Promise((/** @type {(value?: unknown) => void} */ resolve) => {
      const handleTransitionEnd = (/** @type {TransitionEvent} */ event) => {
        if (event.target === dialog && event.propertyName === 'transform') {
          dialog.removeEventListener('transitionend', handleTransitionEnd);
          resolve();
        }
      };

      dialog.addEventListener('transitionend', handleTransitionEnd);

      setTimeout(() => {
        dialog.removeEventListener('transitionend', handleTransitionEnd);
        resolve();
      }, durationMs + 50);
    });

    // Only restore scroll position if no other drawer is opening
    if (!isAnotherDrawerOpening) {
      document.body.style.width = '';
      document.body.style.position = '';
      document.body.style.top = '';
      window.scrollTo({ top: this.#previousScrollY, behavior: 'instant' });
    } else {
      // Another drawer is opening - don't remove fixed positioning
      // Keep body fixed so the scroll position is preserved in body.style.top
      // The new drawer will read from body.style.top when it opens
    }

    dialog.close();
    dialog.classList.remove('dialog-closing');

    this.dispatchEvent(new CustomEvent('dialog:close'));
  };

  /**
   * Toggle the drawer open/closed
   */
  toggleDialog() {
    if (this.refs.dialog.open) {
      this.closeDialog();
    } else {
      this.showDialog();
    }
  }
}

if (!customElements.get('search-drawer-component')) {
  customElements.define('search-drawer-component', SearchDrawerComponent);
}

